import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L, { LatLngExpression } from "leaflet";
import { api } from "@/lib/trpc/client";
import { Button } from "@/components/ui/button";
import { convertToPolygon, getCentroid, isInPolygon } from "./utils/location-picker-constants";
import { FormItem } from "@/components/ui/form";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { translations } from "@/messages";
import { useLocale } from "use-intl";
import { useTranslations } from "use-intl";
import "leaflet-geosearch/dist/geosearch.css";
import LocationPickerMap from "./utils/LocationPickerMap";
type LocationPickerProps = {
  disabled?: boolean;
  initialCoordinates?: number[];
  onChange: (newCoordinates: number[]) => void;
  type?: "static" | "dynamic" | undefined;
  locationPolygonSlug?: string;
  geometryId?: string;
  customErrorMessage?: string;
  title: string;
  showSearch?: boolean;
};
const LocationPicker = ({
  type,
  geometryId,
  disabled,
  initialCoordinates,
  locationPolygonSlug,
  onChange,
  customErrorMessage,
  title,
  showSearch = false
}: LocationPickerProps) => {
  const t = useTranslations("common");
  const locale = useLocale() as keyof typeof translations;
  const [initialPosition, setInitialPosition] = useState<LatLngExpression | null>(null);
  const [mapViewable, setMapViewable] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(12);
  const [error, setError] = useState<string>("");
  const getLocationPolygonQuery = useCallback(() => {
    if (type === "static") {
      return api.locationsRouter.getLocationPolygonCoordinates.useQuery({
        id: geometryId!
      }, {
        enabled: !!geometryId
      });
    } else {
      return api.locationsRouter.getLocationPolygonBySlug.useQuery({
        slug: locationPolygonSlug!
      }, {
        enabled: !!locationPolygonSlug
      });
    }
  }, [type, geometryId, locationPolygonSlug]);
  const locationPolygonQuery = getLocationPolygonQuery();
  const locationPolygon = useMemo(() => convertToPolygon(locationPolygonQuery.data), [locationPolygonQuery.data]) as GeoJSON.Polygon;
  const markerRef = useRef<L.Marker>();
  const markerGroupsRef = useRef<L.LayerGroup>();
  const previewMarkerRef = useRef<L.Marker>();
  const previewMarkerGroupsRef = useRef<L.LayerGroup>();
  useEffect(() => {
    if (locationPolygonQuery.isLoading) {
      return;
    }

    // @ts-expect-error
    const initialPos = initialCoordinates || locationPolygon && getCentroid(locationPolygon);
    if (!initialPos) {
      navigator.geolocation.getCurrentPosition(position => {
        setInitialPosition([position.coords.latitude, position.coords.longitude]);
      }, () => {
        setInitialPosition([0, 0]);
      });
    } else {
      setInitialPosition(initialPos as LatLngExpression);
    }
  }, [initialCoordinates, locationPolygon, locationPolygonQuery.isLoading]);
  const saveLocation = () => {
    if (!markerRef.current?.getLatLng || !locationPolygon && !!type) {
      setError(translations[locale]["formView"]["errorMessage"]);
      return;
    }
    const markerLatLng = markerRef.current.getLatLng();
    const insidePolygon = !!type ? isInPolygon([markerLatLng.lat, markerLatLng.lng], locationPolygon) : true;
    if (insidePolygon) {
      const mapLocation = [markerLatLng.lat, markerLatLng.lng];
      onChange(mapLocation);
      setInitialPosition(mapLocation as LatLngExpression);
      setMapViewable(false);
      setError("");
      if (previewMarkerRef.current && previewMarkerGroupsRef.current) {
        previewMarkerRef.current.setLatLng(mapLocation as LatLngExpression);
      }
    } else {
      setError(customErrorMessage || translations[locale]["formView"]["locationNotInsidePolygon"]);
    }
  };
  if (!initialPosition) {
    return null;
  }
  return <div data-sentry-component="LocationPicker" data-sentry-source-file="LocationPickerRaw.tsx">
      <div className="flex items-center gap-2">
        <FormItem data-sentry-element="FormItem" data-sentry-source-file="LocationPickerRaw.tsx">{title}</FormItem>
      </div>
      <Dialog open={mapViewable} onOpenChange={setMapViewable} data-sentry-element="Dialog" data-sentry-source-file="LocationPickerRaw.tsx">
        <div className="relative h-[60vh] w-full">
          <div onClick={() => setMapViewable(true)} className="absolute z-[9000] flex h-full w-full cursor-pointer items-center justify-center bg-[rgba(0,0,0,0)] p-4 text-[rgba(255,255,255,0)] transition-all hover:bg-[rgba(0,0,0,0.7)] hover:text-white">
            {translations[locale]["formView"]["clickToOpenMap"]}
          </div>
          {!mapViewable && initialPosition && <LocationPickerMap initialPosition={initialPosition || [0, 0]} locationPolygon={locationPolygon} type={type} markerRef={previewMarkerRef} markerGroupsRef={previewMarkerGroupsRef} zoom={zoom} setZoom={setZoom} showSearch={false} disabled />}
        </div>
        <DialogContent className="z-[9999] gap-0 xs:w-[90vw] xs:max-w-[90vw] tablet:w-[60vw] tablet:max-w-[60vw]" data-sentry-element="DialogContent" data-sentry-source-file="LocationPickerRaw.tsx">
          <div className="relative h-[60vh] w-full p-4">
            <LocationPickerMap initialPosition={initialPosition || [0, 0]} locationPolygon={locationPolygon} type={type} markerRef={markerRef} markerGroupsRef={markerGroupsRef} zoom={zoom} setZoom={setZoom} showSearch={showSearch} disabled={disabled} data-sentry-element="LocationPickerMap" data-sentry-source-file="LocationPickerRaw.tsx" />
          </div>
          <Button onClick={saveLocation} className="mx-4" data-sentry-element="Button" data-sentry-source-file="LocationPickerRaw.tsx">
            {t("save")}
          </Button>
          <div>{error && <p className="mx-4 text-red-500">{error}</p>}</div>
        </DialogContent>
      </Dialog>
    </div>;
};
export default React.memo(LocationPicker);